import { mcpClient, getMcpProjectId } from '../lib/client';
import { AP_API_BASE_URL } from '../lib/config';
import { MCP_ENDPOINTS } from './mcp.utils';
import type {
    ProjectMcpServer,
    McpServerStatus,
} from './mcp.types';
import type { ApiResponse } from '../lib/types';

// Helper type for error handling
type ApiError = {
    response?: {
        data?: {
            message?: string;
        };
        status?: number;
    };
};

/**
 * Fetches the MCP server for the current project.
 * Returns null data (no error) when the server hasn't been enabled yet.
 */
export const getProjectMcpServer = async (): Promise<ApiResponse<ProjectMcpServer | null>> => {
    try {
        const projectId = await getMcpProjectId();
        if (!projectId) return { error: 'Could not resolve project ID', status: 401 };

        const response = await mcpClient.get(MCP_ENDPOINTS.MCP_SERVER_BY_PROJECT(projectId));
        return { data: response.data };
    } catch (error: unknown) {
        const status = (error as ApiError).response?.status;
        if (status === 404) return { data: null };
        return {
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to fetch MCP server',
            status: status || 500,
        };
    }
};

/**
 * Enables or disables the project's MCP server.
 * Also creates the server record on the first call.
 */
export const updateProjectMcpStatus = async (status: McpServerStatus): Promise<ApiResponse<ProjectMcpServer>> => {
    try {
        const projectId = await getMcpProjectId();
        if (!projectId) return { error: 'Could not resolve project ID', status: 401 };

        const response = await mcpClient.post(MCP_ENDPOINTS.MCP_SERVER_BY_PROJECT(projectId), { status });
        return { data: response.data };
    } catch (error: unknown) {
        return {
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to update MCP server status',
            status: (error as ApiError).response?.status || 500,
        };
    }
};

/**
 * Rotates the project MCP server token.
 */
export const rotateProjectMcpToken = async (): Promise<ApiResponse<ProjectMcpServer>> => {
    try {
        const projectId = await getMcpProjectId();
        if (!projectId) return { error: 'Could not resolve project ID', status: 401 };

        const response = await mcpClient.post(MCP_ENDPOINTS.ROTATE_PROJECT_MCP_TOKEN(projectId));
        return { data: response.data };
    } catch (error: unknown) {
        return {
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to rotate token',
            status: (error as ApiError).response?.status || 500,
        };
    }
};

/**
 * Toggles an individual flow's ENABLED/DISABLED status on the MCP server.
 */
export const updateFlowStatus = async (flowId: string, status: McpServerStatus): Promise<ApiResponse<{ id: string; status: string }>> => {
    try {
        const response = await mcpClient.post(MCP_ENDPOINTS.FLOW_BY_ID(flowId), { status });
        return { data: response.data };
    } catch (error: unknown) {
        return {
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to update flow status',
            status: (error as ApiError).response?.status || 500,
        };
    }
};

/**
 * Returns the HTTP endpoint URL for the project's MCP server.
 * e.g. https://workflow.voicebot.studio/api/v1/projects/{projectId}/mcp-server/http
 */
export const getProjectMcpHttpUrl = (projectId: string): string =>
    MCP_ENDPOINTS.MCP_HTTP_SERVER_URL(AP_API_BASE_URL, projectId);
